import * as SeparatorPrimitive from '@radix-ui/react-separator'
import React from 'react'

import { cn } from '../../lib/utils'
import { Badge } from '../Badge'

/**
 * Seperator component originally based on the Shadcn UI Separator component.
 * Source: https://ui.shadcn.com/docs/components/separator
 *
 * @param className - The class name to apply to the separator.
 * @param orientation - The orientation of the separator.
 * @param decorative - Whether the separator is decorative.
 * @param label - Optional label to display in the center of the separator.
 * @param props - The props to apply to the separator.
 */
export const Separator = ({
  className,
  orientation = 'horizontal',
  decorative = true,
  label,
  ...props
}: React.ComponentProps<typeof SeparatorPrimitive.Root> & {
  label?: string
}) => {
  const separator = (
    <SeparatorPrimitive.Root
      data-slot="separator"
      decorative={label ? true : decorative}
      orientation={orientation}
      className={cn(
        'bg-panel-3 shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px',
        !label && className,
      )}
      {...props}
    />
  )

  // If label is provided and orientation is horizontal, render with label
  if (label && orientation === 'horizontal') {
    return (
      <div
        className={cn('relative flex items-center', className)}
        role="separator"
      >
        {/* Left line */}
        <div className="flex-1 h-px bg-border-strong" />

        {/* Center pill with label */}
        <div className="px-2 py-1 mx-2">
          <Badge tone="neutral">
            <span className="px-2 select-none">{label}</span>
          </Badge>
        </div>

        {/* Right line */}
        <div className="flex-1 h-px bg-border-strong" />
      </div>
    )
  }

  return separator
}
